import useUserVarStore from "@app/store/useUserVarStore";
import ShareIcon from "@assets/quick-news/share.svg";
import ReactionToolbar from "@components/Blocks/ReactionToolbar/ReactionToolbar";
import {
  ADDITION_LARGE,
  ADDITION_MEDIUM,
  ADDITION_SMALL,
  QN_SOCIAL_ICON_LIST,
  SIZE_THRESHOLD_MEDIUM,
  SIZE_THRESHOLD_SMALL,
} from "@config/quickNews";
import { Image } from "@elements/ResponsiveImage/Image";
import Date from "@elements/Typography/Date";
import getFallbackSectionImage from "@helper/getFallbackSectionImage";
import { capitalizeFirstCharOnly } from "@helper/stringTransform";
import {
  findStyleBySectionAndSubCategory,
  findStyleBySectionNew,
} from "@helper/utils";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { useArticleReactions } from "@hooks/useArticleReactions";
import { ShareSocialBar } from "@routes/Article/Components/ShareSocialBar";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { GalleryCarouselTypes } from "@typings/Opensearch.d";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import QnParagraph from "./QnParagraph";

export default function QnArticleCard({
  item,
  typeIndex,
  isMobile,
}: {
  item: ProcessedArticleData;
  typeIndex: number;
  isMobile?: boolean;
}) {
  const { userToken } = useUserVarStore();

  const {
    sectionName,
    title,
    date,
    datetime,
    time,
    articleBody,
    linkTo,
    urlPath,
    imageGallery,
    id,
    parentSection,
    uniqueSectionName,
  } = item;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: datetime,
    uniqueSectionName: sectionName,
  });

  const BHCustomEvent = useBHCustomEventAnalyticsCall();

  const renderItems = imageGallery.filter(
    (card) => card.type === GalleryCarouselTypes.IMAGE,
  );
  const imageItem = renderItems.length > 0 ? renderItems[0] : null;

  const dummyImage = getFallbackSectionImage(sectionName);
  const { selectedColor } = parentSection
    ? findStyleBySectionAndSubCategory(parentSection) || {}
    : findStyleBySectionNew(uniqueSectionName) || {};

  const textColor = selectedColor ? `text-${selectedColor}` : "";

  const [isDescriptionOpen, setDescriptionIsOpen] = useState(false);
  const [reduceSize, setReduceSize] = useState(100);

  // Ref to image element (safer than querySelector)
  const imgRef = useRef<HTMLImageElement | null>(null);

  const handleOnReadMoreClick = () => {
    BHCustomEvent(
      "quick-news",
      `read full article::${typeIndex + 1}`,
      item.title,
      undefined,
      "",
    );
  };

  // Toggle article description
  const toggleDescription = () => {
    setDescriptionIsOpen((prev) => !prev);
  };

  function getAddition(reduceSize: number): number {
    if (reduceSize < SIZE_THRESHOLD_SMALL) return ADDITION_SMALL;
    if (reduceSize < SIZE_THRESHOLD_MEDIUM) return ADDITION_MEDIUM;
    return ADDITION_LARGE;
  }

  // Smooth height adjustment for article image
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    if (isDescriptionOpen) {
      const addition = getAddition(reduceSize);
      el.style.height = `${el.offsetHeight - (reduceSize + addition)}px`;
    } else {
      el.style.removeProperty("height");
    }
  }, [isDescriptionOpen, reduceSize, id]);

  return (
    <div
      data-testid="qn-article-card"
      className="relative grid h-full w-full bg-bg-primary pb-5"
      style={{ gridTemplateRows: "auto 1fr" }}
    >
      {/* Article Image */}
      <div className="relative">
        <Image
          ref={imgRef}
          src={imageItem?.image?.url ?? dummyImage}
          displayWidth={450}
          srcWidth={450}
          srcHeight={isDescriptionOpen ? 108 : 300}
          alt={imageItem?.altText ?? title}
          aspectRatio="auto"
          className="qn-article-img w-full h-[270px] md:h-[280px] xs:h-[230px] duration-500 pointer-events-none"
        />
      </div>

      {/* Content Block */}
      <div className="px-4 pt-2.5 text-sm text-neutral-700 md:text-base">
        <div className="pb-3 text-xs font-semibold">
          <span className={`text-[12px] font-semibold ${textColor}`}>
            {capitalizeFirstCharOnly(sectionName || "")}
          </span>
          <span className="float-right">
            {/* {datetime} */}
            <Date
              date={date}
              time={time}
              variant={"or"}
              className="text-nowrap text-[12px] md:text-[12px] text-text-muted font-semimedium leading-[21px]"
            />
          </span>
        </div>

        <h1 className="qn-article-title line-clamp-3 text-2md font-medium leading-[120%] text-text-body mb-2.5">
          {title}
        </h1>

        <div className="text-text-body">
          <QnParagraph
            articleBody={articleBody}
            toggleDescription={toggleDescription}
            isDescriptionOpen={isDescriptionOpen}
            setReduceSize={setReduceSize}
          />
        </div>
      </div>

      {/* Action Bar */}
      <div className="actions absolute bottom-0 z-30 flex w-full items-center justify-between gap-2 bg-bg-primary px-4 py-4">
        <Link
          reloadDocument
          to={`${urlPath ? urlPath : linkTo}${isMobile ? "?ref=quick-news" : ""}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOnReadMoreClick}
          className="flex h-xl w-[80%] flex-[1_0_0] items-center justify-center gap-2 bg-brand-primary text-center text-xs font-regular leading-[120%] text-text-inverse"
        >
          BACA ARTIKEL PENUH
        </Link>

        <ReactionToolbar
          articleId={item.id}
          showReactions={showReactions}
          reactions={reactions}
          userReactions={userReactions}
          reactionsError={reactionsError}
          handleReactionClick={handleReactionClick}
          // expandStyle="-top-24 -right-12"
          qnStyle={true}
        />

        {/* Divider only when reactions are visible */}
        {showReactions && !reactionsError && (
          <div className="hidden h-[32px] w-[1px] lg:block" />
        )}

        <ShareSocialBar
          dateCreated={datetime}
          pageUrl={`/quick-news/${id}`}
          socialIconList={QN_SOCIAL_ICON_LIST}
          title={title}
          className={"!top-[-55px]"}
          renderButton={(onClick) => (
            <button onClick={onClick} className="pt-1.5">
              <img src={ShareIcon} alt="Share" width={24} height={24} />
            </button>
          )}
          onClickGaEvent={() => {
            BHCustomEvent(
              "quick-news",
              `share::${typeIndex + 1}`,
              title,
              undefined,
              "",
            );
          }}
        />
      </div>
    </div>
  );
}
